import type { HeadsUp, Item } from '@hugo/core'
import { Link } from '@tanstack/react-router'
import {
  BellRingIcon,
  CalendarIcon,
  ClockAlertIcon,
  ExternalLinkIcon,
  InboxIcon,
  MegaphoneIcon,
  SunIcon,
  TreePalmIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { FreedTimeCard } from '@/components/freed-time-card'
import { ItemRow } from '@/components/item-row'
import { TimeOffSuggestionView } from '@/components/time-off-suggestion'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { View } from '@/components/view'
import { useWorkspace } from '@/components/workspace'
import { ApiError } from '@/lib/api'
import { useSettings, useToday } from '@/lib/items'
import { headsUpReason, headsUpTitle, userToday } from '@/lib/plan'
import { useUserNow } from '@/lib/session'
import { attention } from '@/lib/styles'
import { meetingOver, meetingTime, plannedNote, todayDuration, todaySummary } from '@/lib/today'
import { cn } from '@/lib/utils'

/** A titled part of the day with its count. */
function Section({
  id,
  title,
  count,
  children,
  footer,
}: {
  id: string
  title: string
  count?: number
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <section
      aria-labelledby={`today-${id}-heading`}
      className="flex flex-col gap-2"
      data-testid={`today-${id}`}
    >
      <h2
        id={`today-${id}-heading`}
        className="flex items-baseline gap-2 text-sm font-medium text-muted-foreground"
      >
        {title}
        {count !== undefined && count > 0 ? (
          <span className="text-xs tabular-nums" data-testid={`today-${id}-count`}>
            {count}
          </span>
        ) : null}
      </h2>
      {children}
      {footer}
    </section>
  )
}

/** Three numbers of the day, from the planner. */
function TimeStrip({
  capacity,
  meetings,
  working,
}: {
  capacity: number
  meetings: number
  working: number
}) {
  const cell = (label: string, value: string, testId: string) => (
    <div className="flex min-w-0 flex-col gap-0.5 p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className="font-heading text-lg font-semibold whitespace-nowrap tabular-nums"
        data-testid={testId}
      >
        {value}
      </dd>
    </div>
  )
  return (
    <dl
      className="grid grid-cols-3 divide-x overflow-hidden rounded-xl border"
      data-testid="today-time"
    >
      {cell('For tasks', todayDuration(capacity), 'today-capacity')}
      {cell('Meetings', meetings > 0 ? todayDuration(meetings) : 'None', 'today-meetings-time')}
      {cell('Work left', todayDuration(working), 'today-working')}
    </dl>
  )
}

const HEADS_UP_ICONS = {
  owe_late: ClockAlertIcon,
  waiting_away: TreePalmIcon,
  waiting_followup: BellRingIcon,
} as const

/** Who to tell or remind: opens the item, where its panel sends or copies the text. */
function HeadsUpLine({ headsUp, today, year }: { headsUp: HeadsUp; today: string; year: number }) {
  const { openDetails } = useWorkspace()
  const Icon = HEADS_UP_ICONS[headsUp.kind]
  return (
    <li>
      <button
        type="button"
        onClick={() => openDetails(headsUp.itemId)}
        className="flex w-full items-start gap-3 px-3 py-2.5 text-left outline-none hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-inset"
        data-testid="today-headsup"
        data-kind={headsUp.kind}
      >
        <Icon aria-hidden="true" className={cn('mt-0.5 size-4 shrink-0', attention.icon)} />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium">
            {headsUpTitle(headsUp)}
            <span className="font-normal text-muted-foreground"> · {headsUp.title}</span>
          </span>
          <span className="text-xs text-muted-foreground">
            {headsUpReason(headsUp, today, year)}
          </span>
        </span>
      </button>
    </li>
  )
}

function ItemList({
  items,
  now,
  timeZone,
  notes,
}: {
  items: Item[]
  now: Date
  timeZone: string
  notes?: Map<string, string>
}) {
  const { openDetails } = useWorkspace()
  const open = (item: Item) => openDetails(item.id)
  return (
    <ul className="divide-y overflow-hidden rounded-xl border">
      {items.map((item) => (
        <li key={item.id}>
          <ItemRow
            item={item}
            mode={item.status === 'snoozed' ? 'snoozed' : 'list'}
            selected={false}
            now={now}
            timeZone={timeZone}
            onSelect={open}
            onOpen={open}
            note={notes?.get(item.id)}
          />
        </li>
      ))}
    </ul>
  )
}

/**
 * Today: the day from now, from hugo's planner. How much time is left for tasks and promises
 * after meetings, who to tell or remind, what hugo plans to do today, what else is due, the
 * follow-ups that came, the meetings, and what waits in the inbox. Rows open the details.
 */
export function TodayPage() {
  const query = useToday()
  const settings = useSettings()
  const timeZone = settings.data?.timezone ?? 'Europe/Zurich'
  const now = useUserNow(30_000)
  const { today, year } = userToday(now, timeZone)
  const brief = query.data
  const state = query.isPending ? 'loading' : query.isError ? 'error' : 'ready'

  return (
    <View
      name="today"
      state={state}
      title="Today"
      description={brief ? todaySummary(brief, year) : 'Your day, from hugo’s plan.'}
    >
      {query.error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load today</AlertTitle>
          <AlertDescription>
            {query.error instanceof ApiError
              ? query.error.message
              : 'Something went wrong. Please try again.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {!brief ? (
        query.isPending ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        ) : null
      ) : (
        <>
          {brief.workday ? (
            <TimeStrip
              capacity={brief.time.capacityMinutes}
              meetings={brief.time.meetingMinutes}
              working={brief.time.workingMinutes}
            />
          ) : null}
          {!brief.calendarConnected ? (
            <p className="text-sm text-muted-foreground" data-testid="today-no-calendar">
              <CalendarIcon aria-hidden="true" className="mr-1 inline size-4 align-text-bottom" />
              Connect Google Calendar in{' '}
              <Link
                to="/settings/$section"
                params={{ section: 'apps' }}
                className="font-medium underline-offset-4 hover:underline"
              >
                Settings
              </Link>{' '}
              so hugo plans around your meetings.
            </p>
          ) : null}

          {brief.freedTime ? (
            <FreedTimeCard freed={brief.freedTime} date={brief.date} timeZone={timeZone} />
          ) : null}

          {brief.timeOffSuggestions.map((suggestion) => (
            <TimeOffSuggestionView
              key={suggestion.key}
              suggestion={suggestion}
              thisYear={year}
              layout="callout"
            />
          ))}

          {brief.headsUps.length > 0 ? (
            <Section
              id="send"
              title="Tell and remind"
              count={brief.headsUps.length}
              footer={
                <div>
                  <Button
                    variant="link"
                    size="sm"
                    className="h-8 px-0"
                    render={<Link to="/outbox" />}
                  >
                    <MegaphoneIcon aria-hidden="true" />
                    Open the Outbox
                  </Button>
                </div>
              }
            >
              <ul className="divide-y overflow-hidden rounded-xl border">
                {brief.headsUps.map((headsUp) => (
                  <HeadsUpLine key={headsUp.episode} headsUp={headsUp} today={today} year={year} />
                ))}
              </ul>
            </Section>
          ) : null}

          <Section id="plan" title="hugo's plan for today" count={brief.planned.length}>
            {brief.planned.length > 0 ? (
              <ItemList
                items={brief.planned.map((p) => p.item)}
                now={now}
                timeZone={timeZone}
                notes={new Map(brief.planned.map((p) => [p.item.id, plannedNote(p.minutes)]))}
              />
            ) : (
              <Empty className="flex-none border border-dashed py-8" data-testid="today-plan-empty">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <SunIcon aria-hidden="true" />
                  </EmptyMedia>
                  <EmptyTitle>Nothing planned for today</EmptyTitle>
                  <EmptyDescription>
                    {brief.workday
                      ? 'Give tasks and promises a date, or put them on a day on the board, and hugo plans them into your working days.'
                      : 'No working hours today: hugo plans nothing for today.'}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </Section>

          {brief.due.length > 0 ? (
            <Section id="due" title="Also due, no time today" count={brief.due.length}>
              <ItemList items={brief.due} now={now} timeZone={timeZone} />
            </Section>
          ) : null}

          {brief.personal.length > 0 ? (
            <Section id="personal" title="Personal" count={brief.personal.length}>
              <ItemList items={brief.personal} now={now} timeZone={timeZone} />
            </Section>
          ) : null}

          {brief.followUps.length > 0 ? (
            <Section id="followups" title="Follow up" count={brief.followUps.length}>
              <ItemList items={brief.followUps} now={now} timeZone={timeZone} />
            </Section>
          ) : null}

          {brief.meetings.length > 0 ? (
            <Section id="meetings" title="Meetings" count={brief.meetings.length}>
              <ul className="divide-y overflow-hidden rounded-xl border">
                {brief.meetings.map((meeting) => {
                  const over = meetingOver(meeting, now)
                  return (
                    <li
                      key={meeting.id}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5',
                        over && 'text-muted-foreground',
                      )}
                      data-testid="today-meeting"
                      data-over={over}
                    >
                      <span
                        className="w-24 shrink-0 text-sm tabular-nums"
                        data-testid="today-meeting-time"
                      >
                        {meetingTime(meeting, brief.date, timeZone)}
                      </span>
                      <span className="min-w-0 flex-1 text-sm font-medium break-words">
                        {meeting.kind === 'out_of_office'
                          ? (meeting.title ?? 'Out of office')
                          : (meeting.title ?? '(no title)')}
                      </span>
                      {meeting.url ? (
                        <a
                          href={meeting.url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`Open ${meeting.title ?? 'the meeting'} in Google Calendar`}
                          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50"
                        >
                          <ExternalLinkIcon aria-hidden="true" className="size-4" />
                        </a>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </Section>
          ) : null}

          {brief.inbox > 0 ? (
            <div>
              <Button variant="outline" render={<Link to="/inbox" />} data-testid="today-inbox">
                <InboxIcon aria-hidden="true" />
                {brief.inbox === 1
                  ? '1 item to sort in the inbox'
                  : `${brief.inbox} items to sort in the inbox`}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </View>
  )
}
